import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import Grid from "@mui/material/Grid"
import Card from "@mui/material/Card"
import IconButton from "@mui/material/IconButton"
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import TextField from "@mui/material/TextField"

import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"

import {
  fetchEmpresasById,
  fetchCatalogoConfiguraciones,
  fetchDiccionarioByEmpresaId,
  updateDiccionarioEmpresa,
} from "./service"

import DashboardLayout from "monitor/LayoutContainers/DashboardLayout"
import DashboardNavbar from "monitor/Navbars/DashboardNavbar"
import Footer from "monitor/Footer"
import { showToast } from "utils/swalToast"

function AddDiccionario() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [current, setCurrent] = useState()
  const [catalogo, setCatalogo] = useState([])
  const [rows, setRows] = useState([])

  const selectedIds = rows.map((r) => r.idConfiguracion).filter((v) => v !== "" && v != null)
  const uniqueSelected = new Set(selectedIds)
  const remainingCount = Math.max(0, catalogo.length - uniqueSelected.size)
  const disableAdd = remainingCount === 0

  useEffect(() => {
    fetchCatalogoConfiguraciones("EMPRESA").then(setCatalogo)
    if (id) {
      fetchEmpresasById(id).then(setCurrent)
      fetchDiccionarioByEmpresaId(id).then((data) => setRows(data || []))
    }
  }, [id])

  const getCatalogoOptions = (rowIndex) => {
    const currentValue = rows[rowIndex]?.idConfiguracion
    const takenByOtherRows = new Set(
      rows
        .filter((_, idx) => idx !== rowIndex)
        .map((r) => r.idConfiguracion)
        .filter((v) => v !== "" && v != null)
    )
    return catalogo.filter(
      (c) => !takenByOtherRows.has(c.idConfiguracion) || c.idConfiguracion === currentValue
    )
  }

  const handleAddRow = () => {
    if (disableAdd) return
    setRows([...rows, { idConfiguracion: "", clave: "", tipo: "", descripcion: "", valor: "" }])
  }

  const handleRemoveRow = (index) => {
    setRows(rows.filter((_, idx) => idx !== index))
  }

  const handleSelectKey = (index, idConfiguracion) => {
    const catalogoItem = catalogo.find((c) => c.idConfiguracion === idConfiguracion)
    const updated = rows.map((row, idx) =>
      idx === index
        ? {
            ...row,
            idConfiguracion,
            clave: catalogoItem?.clave || "",
            tipo: catalogoItem?.tipo || "",
            descripcion: catalogoItem?.descripcion || "",
          }
        : row
    )
    setRows(updated)
  }

  const handleValorChange = (index, valor) => {
    setRows(rows.map((row, idx) => (idx === index ? { ...row, valor } : row)))
  }

  const tipoLabel = (tipo) => {
    switch (tipo) {
      case "NUMERICO":
        return "Numérico"
      case "LISTA_CORREOS":
        return "Lista Correos"
      case "LISTA_TEXTO":
        return "Lista Texto"
      case "TEXTO":
        return "Texto"
      default:
        return ""
    }
  }

  const handleSave = async () => {
    const incompletas = rows.some((r) => !r.idConfiguracion || !`${r.valor ?? ""}`.trim())
    if (incompletas) {
      showToast({ icon: "warning", title: "Atención", text: "Complete la llave y el valor de cada fila" })
      return
    }
    try {
      const payload = rows.map((r) => ({ idConfiguracion: r.idConfiguracion, valor: r.valor }))
      const result = await updateDiccionarioEmpresa(id, payload)
      setRows(result || [])
      showToast({ title: "Diccionario guardado", icon: "success" })
      navigate("/empresas")
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={11}>
            <Card>
              <MDBox p={2}>
                <MDTypography variant="h5">Diccionario de Parámetros</MDTypography>
                <MDTypography variant="button" color="text">
                  {current?.descripcion}
                </MDTypography>
              </MDBox>
              <MDBox p={2}>
                {rows.map((row, index) => (
                  <Grid container spacing={2} mt={0.5} key={index} alignItems="flex-start">
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <InputLabel>Parámetro</InputLabel>
                        <Select
                          label="Parámetro"
                          variant="outlined"
                          fullWidth
                          value={row.idConfiguracion}
                          onChange={(e) => handleSelectKey(index, e.target.value)}
                          sx={{ p: 1.5 }}
                        >
                          <MenuItem value="">Seleccione..</MenuItem>
                          {getCatalogoOptions(index).map((c) => (
                            <MenuItem key={c.idConfiguracion} value={c.idConfiguracion}>
                              {c.clave}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        label="Tipo"
                        variant="outlined"
                        fullWidth
                        value={tipoLabel(row.tipo)}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Valor"
                        variant="outlined"
                        fullWidth
                        value={row.valor ?? ""}
                        onChange={(e) => handleValorChange(index, e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={10} md={3}>
                      <TextField
                        label="Información (lectura)"
                        variant="outlined"
                        fullWidth
                        multiline
                        maxRows={4}
                        value={row.descripcion ?? ""}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2} md={1}>
                      <IconButton color="error" onClick={() => handleRemoveRow(index)}>
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                <MDBox textAlign="center" mt={3}>
                  <MDButton
                    onClick={handleAddRow}
                    variant="gradient"
                    color="info"
                    disabled={disableAdd}
                  >
                    Agregar Fila
                  </MDButton>
                  <MDButton onClick={handleSave} variant="gradient" color="success" sx={{ ml: 2 }}>
                    Confirmar Diccionario
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  )
}

export default AddDiccionario
